import React, { useEffect, useState } from "react";

import { useNavigate } from "react-router-dom";

import { DateTime } from "luxon";

import { useTranslation } from "react-i18next";

import Spinner from "../components/Spinner";

import ErrorModal from "../components/modals/ErrorModal";

import LanguageToggle from "../components/buttons/LanguageToggle";

import { assetService } from "../services/assetService";
import { transactionService } from "../services/transactionService";
import { partnerService, Partner } from "../services/partnerService";
import { partnerWellSuperAppService } from "../services/partnerWellSuperAppService";
import { partnerAliveService } from "../services/partnerAliveService";
import { partnerSatService } from "../services/partnerSatService";
import { partnerInterpreterService } from "../services/partnerInterpreterService";

import { UserData } from "../model/User";

const Register: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const i18nPath = "pages.register";

  const [logo, setLogo] = useState<string | undefined>();
  const [partner, setPartner] = useState<Partner | undefined>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<{ visible: boolean; message: string }>({
    visible: false,
    message: "",
  });

  const [registrationModel, setRegistrationModel] = useState({
    HNNumber: "",
    FirstName: "",
    LastName: "",
    IDCard: "",
    BirthDate: "",
    Gender: "",
    Tel: "",
    Email: "",
    Contact: "",
    AgreementName: "UsageTerms",
  });

  const registrationForm = [
    {
      key: "FirstName",
      label: `${t(`${i18nPath}.fields.firstName`)} *`,
      placeholder: t(`${i18nPath}.placeholders.firstName`),
    },
    {
      key: "LastName",
      label: `${t(`${i18nPath}.fields.lastName`)} *`,
      placeholder: t(`${i18nPath}.placeholders.lastName`),
    },
    {
      key: "Tel",
      label: `${t(`${i18nPath}.fields.tel`)} *`,
      placeholder: t(`${i18nPath}.placeholders.tel`),
    },
  ];

  useEffect(() => {
    const p = partnerService.getPartner();
    if (!p) {
      navigate("/");
      return;
    }

    setPartner(p);
    setLogo(assetService.getLogo(p.name));

    if (p.userData) {
      setRegistrationModel((prev) => ({
        ...prev,
        FirstName: p.userData?.firstName || "",
        LastName: p.userData?.lastName || "",
        Tel: p.userData?.tel || "",
      }));
    }

    loadPreviousRegistrationData(p);
  }, []);

  const loadPreviousRegistrationData = (p?: Partner) => {
    try {
      const lastRegisteredUserData = localStorage.getItem(
        "lastRegisteredUserData",
      );
      const lastRegisteredTime = localStorage.getItem("lastRegisteredTime");
      if (!lastRegisteredUserData || !lastRegisteredTime) return;

      const registeredAt = DateTime.fromISO(lastRegisteredTime);
      const diff = DateTime.local().diff(registeredAt, "hours").hours;

      if (diff < 2) {
        const userData = JSON.parse(lastRegisteredUserData);
        const hasPartnerData =
          p?.userData &&
          (p.userData.firstName || p.userData.lastName || p.userData.tel);

        if (!hasPartnerData) {
          setRegistrationModel((prev) => ({
            ...prev,
            FirstName: userData.firstName || "",
            LastName: userData.lastName || "",
            Tel: userData.tel || "",
          }));
        }
      }
    } catch (err) {
      console.error("Error loading previous registration data:", err);
    }
  };

  const validateForm = (): boolean => {
    const { FirstName, LastName, Tel } = registrationModel;

    if (!FirstName) {
      setError({
        visible: true,
        message: t(`${i18nPath}.validations.firstNameRequired`),
      });
      return false;
    }

    if (!LastName) {
      setError({
        visible: true,
        message: t(`${i18nPath}.validations.lastNameRequired`),
      });
      return false;
    }

    if (!Tel) {
      setError({
        visible: true,
        message: t(`${i18nPath}.validations.telRequired`),
      });
      return false;
    }

    return true;
  };

  const submit = async () => {
    if (!partner) return;

    if (!validateForm()) {
      setIsSubmitting(false);
      return;
    }

    setIsSubmitting(true);

    if (validateForm()) {
      sessionStorage.setItem("userData", JSON.stringify(partner.userData));

      // Preserve existing userData values and only update the required fields
      const existingUserData = partner.userData || ({} as UserData);

      partner.userData = {
        // Required fields from the form
        firstName: registrationModel.FirstName,
        lastName: registrationModel.LastName,
        tel: registrationModel.Tel,

        // Preserve other fields from existing userData - no defaults
        idCard: existingUserData.idCard || "",
        birthdate: existingUserData.birthdate || "",
        email: existingUserData.email || "",
        hn: existingUserData.hn || "", // HN will be generated by the server
        gender: existingUserData.gender || "",
        accessToken: existingUserData.accessToken || "",
        idToken: existingUserData.idToken || "",
        searchOption: existingUserData.searchOption || "",
      };

      // No need to process partner user data for HN generation as server will handle it
      partnerService.setPartner(partner);

      // Partner: WellSuperApp.
      if (partnerWellSuperAppService.isActive() && partner.userData) {
        try {
          await partnerWellSuperAppService.onRegisterUpdateUserDataToWell(
            partner.userData,
          );

          setIsSubmitting(false);
        } catch (error) {
          console.error("WellSuperApp update failed:", error);
          // Continue with registration even if WellSuperApp update fails
          setIsSubmitting(false);
        }
      }

      // Always run registration regardless of partner type or previous API calls
      try {
        if (!partner.userData) {
          throw new Error("User data is undefined");
        }

        // Pass hospitalId for WellSuperApp
        const hospitalId =
          partner.name === "WellSuperApp" || partner.name === "Well_AIDOCTOR"
            ? partner.hospitalId
            : undefined;

        await transactionService.register(
          partner.userData,
          partner.userConsent,
          partner.name,
          undefined, // contactName
          false, // isGuardianMode
          hospitalId,
        );

        setIsSubmitting(false);
      } catch (error) {
        console.error("Registration failed:", error);
        setError({
          visible: true,
          message: t(`${i18nPath}.validations.registrationFailed`),
        });
        setIsSubmitting(false);
        return;
      }

      // Partner: Alive, Sat.
      if (partnerAliveService.isActive() || partnerSatService.isActive()) {
        await navigate("/call");
        setIsSubmitting(false);
        return;
      }

      // Partner: Interpreter.
      if (partnerInterpreterService.isActive()) {
        await navigate("/interpreter-select-language");
        setIsSubmitting(false);
        return;
      }

      // Partner: Webview.
      else {
        await navigate("/confirm");
        setIsSubmitting(false);
        return;
      }
    }
  };

  return (
    <div className="flex flex-col items-center p-4 md:p-10 gap-y-4 justify-center min-h-screen w-full">
      <div className="flex flex-col items-center gap-y-4 w-full">
        <div className="w-full max-w-3xl border p-10 rounded-3xl shadow-md bg-white relative">
          <div className="absolute top-4 right-4 z-50">
            <LanguageToggle />
          </div>

          <>
            {/* Header */}
            <div className="flex flex-col gap-y-1 justify-center items-center mb-4">
              {logo && (
                <img
                  src={logo}
                  width={172}
                  height={40}
                  className="mb-4"
                  alt="logo"
                />
              )}
              <div className="text-center">
                <h2 className="text-xl font-semibold">
                  {t(`${i18nPath}.texts.registerTypes.manualRegister.title`)}
                </h2>
              </div>
            </div>

            {/* Form */}
            <div className="mt-6 space-y-4">
              {registrationForm.map((field) => (
                <div key={field.key} className="">
                  <label className="block mb-2 text-md">{field.label}</label>
                  <input
                    type="text"
                    value={(registrationModel as any)[field.key]}
                    onChange={(e) =>
                      setRegistrationModel((prev) => ({
                        ...prev,
                        [field.key]: e.target.value,
                      }))
                    }
                    placeholder={field.placeholder}
                    className="border border-gray-300 py-2 px-3 w-full rounded-2xl focus:outline-none focus:ring-2 focus:ring-[#e0bb84]"
                  />
                </div>
              ))}
              <div className="text-red-500 text-sm">
                {t(`${i18nPath}.texts.noted`)}
              </div>
            </div>

            {/* Submit */}
            <div className="mt-6">
              <button
                onClick={submit}
                disabled={isSubmitting}
                className={`w-full text-white rounded-3xl text-lg transition h-12 flex items-center justify-center ${
                  isSubmitting
                    ? "bg-gray-400 cursor-not-allowed"
                    : "gold-submit-btn"
                }`}
              >
                {isSubmitting ? (
                  <Spinner size="medium" color="white" />
                ) : (
                  t(`${i18nPath}.buttons.submit`)
                )}
              </button>
            </div>

            {/* Footer */}
            <div className="text-center text-gray-400 text-sm mt-8">
              {t("generals.copyright")}
            </div>
          </>

          {/* Error Modal */}
          <ErrorModal
            isVisible={error.visible}
            onConfirm={() => setError({ visible: false, message: "" })}
            onClose={() => setError({ visible: false, message: "" })}
            message={error.message}
            title={t(`${i18nPath}.modals.error.title`)}
            isCancelable={false}
            confirmButtonText={t(`${i18nPath}.modals.error.confirmButton`)}
          />
        </div>
      </div>
    </div>
  );
};

export default Register;
